import server_paginated_table_component from "@/components/probaworkflows_base_components/ServerPaginatedTable/ServerPaginatedTable.vue";
import {
  GENERATE_MANAGED_REPORT_URL,
  GET_REPORT_MANAGEMENT_LOOKUPS_URL,
  LIST_REPORT_TEMPLATES_URL,
  get_headers,
  get_project_id,
} from "@/utils/constants/constants";
import alert_mixin from "@/utils/mixins/alert_mixin";
import axios from "axios";

export default {
  name: "ReportTemplateLibrary",
  components: {
    ServerPaginatedTable: server_paginated_table_component,
  },
  mixins: [alert_mixin],
  props: {
    title: {
      type: String,
      default: "Saved report templates",
    },
    subtitle: {
      type: String,
      default: "Reuse saved templates and generate a report immediately.",
    },
    initial_target_entity_profile_id: {
      type: [Number, String],
      default: null,
    },
  },
  data: () => ({
    headers: get_headers(),
    project_id: get_project_id(),
    loading_templates: false,
    loading_formats: false,
    searching_templates: false,
    generating_template_id: null,
    templates: [],
    output_formats: [],
    selected_output_format_id: "",
    search_query: "",
    search_timer: null,
    last_applied_search: "",
    last_generated_report: null,
    page_number: 1,
    next_page: null,
    prev_page: null,
    total_pages: 1,
    total_count: 0,
    page_size: 10,
    selected_template: null,
  }),
  computed: {
    display_title() {
      return this.translate_label(this.title);
    },
    display_subtitle() {
      return this.translate_label(this.subtitle);
    },
    selected_output_format_label() {
      const selected_output_format = this.output_formats.find(
        (item) => item.id === this.selected_output_format_id
      );
      return selected_output_format?.display_name || "Choose an output";
    },
    summary_cards() {
      return [
        {
          label: this.translate_label("Templates"),
          value: this.total_count || 0,
          description: this.translate_label(
            "Reusable report setups available for this project."
          ),
        },
        {
          label: this.translate_label("Quick generate"),
          value: this.translate_label(this.selected_output_format_label),
          description: this.translate_label(
            "The selected output format is used when you generate directly from the table."
          ),
        },
        {
          label: this.translate_label("Search"),
          value: this.search_query.trim()
            ? this.translate_label("Filtered view")
            : this.translate_label("Showing all templates"),
          description: this.translate_label(
            "Results update automatically while you type."
          ),
        },
      ];
    },
    template_table_headers() {
      return [
        "ID",
        "Template Name",
        "Report Type",
        "Scope",
        "Entity Types",
        "Columns",
        "Questions",
        "Updated",
      ].map((label) => this.translate_label(label));
    },
    detail_column_headers() {
      return ["Order", "Field", "Display Label", "Include Blank"].map(
        (label) => this.translate_label(label)
      );
    },
    detail_question_filter_headers() {
      return [
        "Filter Type",
        "Question",
        "Question Type",
        "Form",
        "Section",
        "Value",
      ].map((label) => this.translate_label(label));
    },
    detail_question_metric_headers() {
      return [
        "Order",
        "Metric",
        "Question",
        "Question Type",
        "Form",
        "Section",
      ].map((label) => this.translate_label(label));
    },
    detail_other_filter_headers() {
      return [
        "Filter Type",
        "Form",
        "Section",
        "Compliance Rule",
        "Value",
      ].map((label) => this.translate_label(label));
    },
    detail_entity_type_headers() {
      return ["ID", "Entity Type"].map((label) => this.translate_label(label));
    },
    table_safe_templates() {
      return this.templates.map((template) => ({
        id: template.id,
        name: template.name,
        report_type: this.translate_label(
          template.report_type?.display_name || "-"
        ),
        scope: this.translate_label(template.scope_type?.display_name || "-"),
        entity_types: this.format_entity_types(template),
        columns: template.columns?.length || 0,
        questions: this.get_question_count(template),
        updated: this.format_date(template.date_modified),
      }));
    },
    detail_entity_types() {
      return (this.selected_template?.entity_type_assignments || []).map((assignment) => ({
        id: assignment.entity_type?.id,
        entity_type_name: assignment.entity_type?.entity_type_name || "-",
      }));
    },
    detail_columns() {
      return (this.selected_template?.columns || []).map((column) => ({
        order: column.order_index,
        field: this.translate_label(column.report_field?.display_name || "-"),
        label: this.get_column_label(column),
        include_blank: column.include_if_blank ? "Yes" : "No",
      }));
    },
    detail_question_filters() {
      return (this.selected_template?.filters || [])
        .filter((filter_row) => filter_row.section_question)
        .map((filter_row) => ({
          filter_type: this.translate_label(
            filter_row.filter_type?.display_name || "-"
          ),
          question: filter_row.section_question?.question_text || "-",
          question_type: this.format_question_type(filter_row.section_question?.question_type),
          form: filter_row.section_question?.form?.form_name || "-",
          section: filter_row.section_question?.section?.name || "-",
          value: this.format_filter_value(filter_row.filter_value),
        }));
    },
    detail_question_metrics() {
      return (this.selected_template?.question_metrics || []).map(
        (question_metric) => ({
          order: question_metric.order_index,
          metric: this.translate_label(
            question_metric.metric_type?.display_name || "-"
          ),
          question:
            question_metric.section_question?.question_text || "-",
          question_type: this.format_question_type(
            question_metric.section_question?.question_type
          ),
          form:
            question_metric.section_question?.form?.form_name || "-",
          section:
            question_metric.section_question?.section?.name || "-",
        })
      );
    },
    detail_other_filters() {
      return (this.selected_template?.filters || [])
        .filter((filter_row) => !filter_row.section_question)
        .map((filter_row) => ({
          filter_type: this.translate_label(
            filter_row.filter_type?.display_name || "-"
          ),
          form: filter_row.form?.form_name || "-",
          section: filter_row.section?.name || "-",
          compliance_rule: filter_row.compliance_rule?.name || "-",
          value: this.format_filter_value(filter_row.filter_value),
        }));
    },
  },
  mounted() {
    this.load_templates();
    this.load_output_formats();
  },
  beforeDestroy() {
    clearTimeout(this.search_timer);
  },
  methods: {
    load_output_formats() {
      this.loading_formats = true;
      axios.get(GET_REPORT_MANAGEMENT_LOOKUPS_URL, {
        headers: this.headers,
        params: { project_id: this.project_id },
      })
        .then((response) => {
          this.output_formats = response.data.data?.output_formats || [];
          this.selected_output_format_id = this.get_preferred_format_id();
        })
        .catch((error) => {
          this.show_error(error.response?.data?.message || "Failed to load output formats");
        })
        .finally(() => {
          this.loading_formats = false;
        });
    },
    build_template_params(page) {
      const params = {
        project_id: this.project_id,
        is_active: true,
        page_number: page,
        page_size: this.page_size,
        search: this.search_query.trim() || undefined,
      };
      return params;
    },
    apply_template_response(response) {
      this.templates = response.data.templates || [];
      this.total_count = response.data.total_count || 0;
      this.page_size = response.data.page_size || this.page_size;
      this.page_number = response.data.page_number || 1;
      this.next_page = response.data.next_page || null;
      this.prev_page = response.data.prev_page || null;
      this.total_pages = response.data.total_pages || 1;
    },
    handle_template_error(error) {
      this.templates = [];
      this.total_count = 0;
      this.show_error(error.response?.data?.message || "Failed to load templates");
    },
    run_template_request(page, loader = null) {
      return axios.get(LIST_REPORT_TEMPLATES_URL, {
        headers: this.headers,
        params: this.build_template_params(page),
      })
        .then((response) => {
          this.apply_template_response(response);
        })
        .catch((error) => {
          this.handle_template_error(error);
        })
        .finally(() => {
          this.loading_templates = false;
          this.searching_templates = false;
          if (loader) {
            return this.close_loader(loader);
          }
          return null;
        });
    },
    load_templates(page = 1, use_loader = true) {
      this.loading_templates = true;
      if (!use_loader) {
        return this.run_template_request(page);
      }
      return this.show_loader("Loading report templates...")
        .then((loader) => this.run_template_request(page, loader));
    },
    get_preferred_format_id() {
      const preferred = this.output_formats.find((item) => item.internal_name === "xlsx");
      if (preferred) {
        return preferred.id;
      }
      return this.output_formats[0]?.id || "";
    },
    get_target_entity_profile_id() {
      const parsed = Number(this.initial_target_entity_profile_id || 0);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    },
    get_question_count(template) {
      return new Set(
        (template.filters || [])
          .filter((item) => item.section_question_id)
          .map((item) => item.section_question_id)
      ).size;
    },
    get_template_by_id(template_id) {
      return this.templates.find((template) => template.id === template_id) || null;
    },
    format_date(date_value) {
      if (!date_value) {
        return "Not available";
      }
      return new Date(date_value).toLocaleString();
    },
    format_entity_types(template) {
      const names = (template.entity_type_assignments || [])
        .map((assignment) => assignment.entity_type?.entity_type_name)
        .filter(Boolean);
      if (!names.length) {
        return this.translate_label("All entity types");
      }
      return names.join(", ");
    },
    get_column_label(column) {
      const default_label = this.translate_label(
        column.report_field?.display_name || "-"
      );
      if (!column.display_label) {
        return default_label;
      }
      if (column.display_label === column.report_field?.display_name) {
        return default_label;
      }
      return column.display_label;
    },
    format_filter_value(filter_value) {
      if (!filter_value) {
        return "-";
      }
      if (Array.isArray(filter_value.values)) {
        return filter_value.values.join(", ");
      }
      if (filter_value.value === true) {
        return "Yes";
      }
      if (filter_value.value === false) {
        return "No";
      }
      return JSON.stringify(filter_value);
    },
    format_question_type(question_type) {
      return String(question_type || "")
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    translate_label(label) {
      return label;
    },
    quick_generate(row) {
      const template = this.get_template_by_id(row.id);
      if (!template || !this.selected_output_format_id) {
        this.show_error("Please select an output format first.");
        return;
      }
      this.generating_template_id = template.id;
      this.last_generated_report = null;
      this.show_loader("Generating report...")
        .then((loader) => axios.post(
          GENERATE_MANAGED_REPORT_URL,
          this.build_generate_payload(template),
          { headers: this.headers }
        )
          .then((response) => {
            this.last_generated_report = response.data.report;
            this.$emit("generated", response.data.report);
            this.show_success("Report generated successfully.");
          })
          .catch((error) => {
            if (error.response?.data?.report) {
              this.last_generated_report = error.response.data.report;
            }
            this.show_error(error.response?.data?.message || "Failed to generate report.");
          })
          .finally(() => {
            this.generating_template_id = null;
            return this.close_loader(loader);
          }));
    },
    build_generate_payload(template) {
      const payload = {
        template_id: template.id,
        output_format_id: this.selected_output_format_id,
      };
      const target_entity_profile_id = this.get_target_entity_profile_id();
      if (target_entity_profile_id) {
        payload.target_entity_profile_id = target_entity_profile_id;
      }
      return payload;
    },
    open_template_details(row) {
      this.selected_template = this.get_template_by_id(row.id);
    },
    close_template_details() {
      this.selected_template = null;
    },
    edit_template(row) {
      this.close_template_details();
      this.$router.push({
        path: "/generate_reports/",
        query: { template_id: row.id },
      });
    },
    go_to_page(page_number) {
      if (!page_number || page_number < 1 || page_number > this.total_pages) {
        return;
      }
      this.load_templates(page_number);
    },
    schedule_search() {
      clearTimeout(this.search_timer);
      this.search_timer = setTimeout(() => {
        this.apply_search();
      }, 250);
    },
    apply_search() {
      const normalized = this.search_query.trim();
      if (normalized === this.last_applied_search) {
        return;
      }
      this.searching_templates = true;
      this.last_applied_search = normalized;
      this.load_templates(1, false);
    },
  },
};
